<script lang="ts">
    import type { Pixel } from "../model/model";
    import { PixelStore } from "../store/PixelStore";
    import { ActiveColorStore } from "../store/ColorStores";
    import { onDestroy, onMount } from "svelte";
    import type { Unsubscriber } from "svelte/store";

    type mode = "draw" | "erase";

    const size = 512;

    let canvas: HTMLCanvasElement;
    let bgCanvas: HTMLCanvasElement;
    let unsubscribe: Unsubscriber;
    let currentMode: mode = "draw";

    let mouseDown = false;

    function pixelPos(event: MouseEvent) {
        const clientRect = canvas.getBoundingClientRect();
        return {
            x: Math.floor((event.clientX - clientRect.left) / (size / 16)),
            y: Math.floor((event.clientY - clientRect.top) / (size / 16)),
        };
    }

    const handleMouseDown = () => (mouseDown = true);
    const handleMouseUp = () => (mouseDown = false);

    function drawCanvas(
        pixels: Pixel[][],
        ctx: CanvasRenderingContext2D,
        isBG = false
    ) {
        for (let i = 0; i < pixels.length; i++) {
            for (let j = 0; j < pixels[i].length; j++) {
                const p = pixels[i][j];
                if (isBG) {
                    if (
                        (i % 2 === 0 && j % 2 === 0) ||
                        (i % 2 === 1 && j % 2 === 1)
                    ) {
                        ctx.fillStyle = `rgba(${164}, ${164}, ${164}, ${255})`;
                    } else {
                        ctx.fillStyle = `rgba(${255}, ${255}, ${255}, ${255})`;
                    }
                } else {
                    ctx.fillStyle = `rgba(${p.r}, ${p.g}, ${p.b}, ${p.a})`;
                }

                const x = j * (size / 16);
                const y = i * (size / 16);
                ctx.fillRect(x, y, size / 16, size / 16);
            }
        }
    }

    function handleClick(event: MouseEvent) {
        const { x, y } = pixelPos(event);
        switch (currentMode) {
            case "draw":
                PixelStore.update(x, y, { ...$ActiveColorStore });
                break;
            case "erase":
                PixelStore.update(x, y, { r: 255, g: 255, b: 255, a: 0 });
                const context = canvas.getContext("2d");
                context.clearRect(
                    x * (size / 16),
                    y * (size / 16),
                    size / 16,
                    size / 16
                );
                break;
        }
    }

    function handleMouseMove(event: MouseEvent) {
        if (mouseDown) {
            handleClick(event);
        }
    }

    function saveCanvasToPNG() {
        const dataURL = canvas.toDataURL();
        const link = document.createElement("a");
        link.download = "pixel-art.png";
        link.href = dataURL;
        link.click();
    }

    onMount(() => {
        const context = canvas.getContext("2d");
        const bgContext = bgCanvas.getContext("2d");
        unsubscribe = PixelStore.subscribe((pixels) =>
            drawCanvas(pixels, context)
        );
        drawCanvas($PixelStore, bgContext, true);
    });

    onDestroy(unsubscribe);
</script>

<div class="canvas-container">
    <canvas class="bg-canvas" bind:this={bgCanvas} width={size} height={size} />
    <canvas
        class="pixelboard"
        bind:this={canvas}
        width={size}
        height={size}
        on:mousemove={handleMouseMove}
        on:mousedown={handleMouseDown}
        on:click={handleClick}
        on:mouseup={handleMouseUp}
    />
</div>

<div class="btn-container">
    <button
        class:active={currentMode === "draw"}
        on:click={() => (currentMode = "draw")}>Draw</button
    >
    <button
        class:active={currentMode === "erase"}
        on:click={() => (currentMode = "erase")}>Erase</button
    >
    <button
        on:click={() => {
            PixelStore.clear();
            const context = canvas.getContext("2d");
            context.clearRect(0, 0, canvas.width, canvas.height);
        }}>Clear</button
    >
    <button on:click={saveCanvasToPNG}>Save</button>
</div>

<style>
    .canvas-container {
        position: relative;
        width: 512px;
        height: 512px;
    }
    .pixelboard,
    .bg-canvas {
        position: absolute;
        top: 0;
        left: 0;
    }

    .btn-container {
        margin: 5px;
    }
</style>
